import { FC } from "react";
import { useTranslation } from "react-i18next";

import {
  getRecipientLines,
  joinParts,
} from "@/components/Portal/Documents/BillingAccounts/billingAccountUtils";
import { DrAccount } from "@/services/model";
import { getCountryAlpha3FromId } from "@/utils/countries";

const DetailBlock: FC<{ label: string; lines: string[] }> = ({
  label,
  lines,
}) => (
  <div className="flex min-w-0 flex-col gap-1">
    <p className="truncate font-semibold leading-md">{label}</p>
    {lines.map((line, index) => (
      <p key={index} className="break-words leading-md">
        {line}
      </p>
    ))}
  </div>
);

interface Props {
  account: DrAccount;
}

/** Referenz/Bemerkung, recipient and postal address of a Vertragskonto. */
export const BillingAccountDetails: FC<Props> = ({ account }) => {
  const { t } = useTranslation("portal", {
    keyPrefix: "user-account.billingAccounts.details",
  });
  const { t: tCountries } = useTranslation("translation", {
    keyPrefix: "countries",
  });
  const address = account.billToAddress;
  const countryId = address?.country?.id;

  const postalLines = [
    joinParts(address?.street, address?.houseNumber),
    joinParts(address?.zip, address?.city),
    countryId == null ? "" : tCountries(getCountryAlpha3FromId(countryId)),
  ].filter((line) => line !== "");

  return (
    <div
      data-testid="billing-account-details"
      className="grid grid-cols-1 gap-4 rounded-md bg-surface-quaternary p-4 text-sm text-foreground-primary tablet:grid-cols-3"
    >
      <div className="flex min-w-0 flex-col gap-2">
        <DetailBlock
          label={t("reference")}
          lines={account.billingReference ? [account.billingReference] : []}
        />
        {account.billingNotes && (
          <DetailBlock label={t("note")} lines={[account.billingNotes]} />
        )}
      </div>
      <DetailBlock label={t("address")} lines={getRecipientLines(address)} />
      <DetailBlock label={t("postalAddress")} lines={postalLines} />
    </div>
  );
};
